/**
 * Formatage français des nombres. Aucune valeur métier ici.
 * Identité MAP v1.0 §5 (ADR 0018) : signe moins U+2212, espace insécable U+00A0 entre le nombre et l'unité et comme séparateur
 * de milliers (Intl produit l'espace fine U+202F, absente d'Atkinson et d'Archivo : remplacée).
 */

/** Espace insécable (U+00A0). */
export const NBSP = "\u00a0";
/** Signe moins typographique (U+2212). */
export const MINUS = "\u2212";

const nf = (decimals: number) =>
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const cache = new Map<number, Intl.NumberFormat>();
function fmtNum(x: number, decimals: number): string {
  let f = cache.get(decimals);
  if (!f) {
    f = nf(decimals);
    cache.set(decimals, f);
  }
  // Intl utilise le tiret « - » (ou U+2212 selon l'ICU) et l'espace fine U+202F : signe moins et espace insécable.
  return f.format(x).replace(/[-\u2212]/u, MINUS).replace(/[\u202f\u2009 ]/gu, NBSP);
}

/** Nombre avec signe explicite (« +1,2 », « −0,4 », « 0 »). */
export function signed(x: number, decimals = 1): string {
  if (Math.abs(x) < Math.pow(10, -decimals) / 2) return fmtNum(0, decimals);
  return (x > 0 ? "+" : "") + fmtNum(x, decimals);
}

/** Nombre sans signe forcé. */
export function num(x: number, decimals = 1): string {
  return fmtNum(x, decimals);
}

/** Md€ courants. */
export function bn(x: number, decimals = 1): string {
  return `${num(x, decimals)}${NBSP}Md€`;
}

export function bnSigned(x: number, decimals = 1): string {
  return `${signed(x, decimals)}${NBSP}Md€`;
}

/** Pourcentage (« 5,1 % »). */
export function pct(x: number, decimals = 1): string {
  return `${num(x, decimals)}${NBSP}%`;
}

export function pctSigned(x: number, decimals = 1): string {
  return `${signed(x, decimals)}${NBSP}%`;
}

/** Points (« +0,3 pt »). */
export function pt(x: number, decimals = 1): string {
  return `${signed(x, decimals)}${NBSP}pt`;
}

/** Entier avec séparateur de milliers (« 12 345 »). */
export function int(x: number): string {
  return fmtNum(Math.round(x), 0);
}

/** Euros arrondis (« 51 200 € »). */
export function eur(x: number): string {
  return `${int(x)}${NBSP}€`;
}

/** Millions avec une décimale (« 2,7 millions »). */
export function millions(x: number): string {
  return `${num(x / 1e6, 1)}${NBSP}million${x >= 2e6 ? "s" : ""}`;
}

/** Coût d'une mesure : >0 = dépense (affichée « −x » car elle creuse le solde), <0 = recette (« +x »). */
export function costAsBalance(costBn: number, decimals = 1): string {
  if (Math.abs(costBn) < 0.05) return "0";
  return (costBn > 0 ? MINUS : "+") + num(Math.abs(costBn), decimals);
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

/** Durée en heures décimales → « 6 h 40 ». */
export function hoursMinutes(h: number): string {
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return mm ? `${hh}${NBSP}h${NBSP}${String(mm).padStart(2, "0")}` : `${hh}${NBSP}h`;
}

/** Variation en % avec signe et unité (« +1,2 % »). */
export function pctDelta(x: number, decimals = 1): string {
  return `${signed(x, decimals)}${NBSP}%`;
}

const UNIT_AFTER_NUMBER = /(\d) (?=(?:Md€|M€|€|%|pts?|h|mois|ans?|an\b|millions?|milliards?|sièges?)(?![\p{L}\d]))/gu;

/**
 * Passe typographique sur un fragment HTML ou un texte déjà composé (ADR 0018) : espace insécable entre un nombre et son unité,
 * U+202F → U+00A0, espace insécable avant « : ; ! ? » et après « (pas de ponctuation orpheline en début de ligne). Rattrape les gabarits qui écrivent « ${x} Md€ » à la main ; les fonctions ci-dessus produisent déjà la
 * bonne forme (signe moins compris : pas de réécriture du trait d'union ici, il apparaît aussi dans les coordonnées SVG).
 */
export function typo(s: string): string {
  return s
    .replace(/\u202f/gu, NBSP)
    .replace(UNIT_AFTER_NUMBER, `$1${NBSP}`)
    .replace(/ ([:;!?»])/gu, `${NBSP}$1`)
    .replace(/« /gu, `«${NBSP}`);
}

/**
 * Libellé court d'une mission LOLF pour les pastilles (« Écologie, développement… » → « Écologie ») : premier mot sans
 * ponctuation finale (red team v0.2 m3 : « Indice de service Écologie, : +0,02 »).
 */
export function missionShort(label: string): string {
  const first = label.trim().split(/\s+/)[0] ?? label;
  return first.replace(/[,;:.…'’-]+$/u, "") || label;
}
